import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Pusher from 'pusher-js';
import {
  Card,
  Layout,
  Text,
  Badge,
  Button,
  Icon,
  BlockStack,
  InlineStack,
  TextField,
  ButtonGroup,
  Tooltip,
  Spinner,
  IndexTable,
  useIndexResourceState,
} from '@shopify/polaris';
import {
  ContentMinor,
  ViewMajor,
  RefreshMajor,
  ChevronLeftMinor,
  ChevronRightMinor,
  QuestionMarkMajor,
} from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';
import '../modal-overrides.css';
import '../dashboard.css';

// Import services
import { storiesService } from '../services/storiesService';
import { generationsService } from '../services/generationsService';
import { authService } from '../services/authService';
import { getThumbnailUrl } from '../utils/imageUtils';
import {
  getMissingSceneOrders,
  isMissingSceneRecoveryRunning,
} from '../utils/generationRecovery';
import { getAddonLabels } from '../utils/orderAddons';
import {
  GENERATION_STATUS_TABS,
  getGenerationStatusTabLabel
} from '../utils/generationStatusCounts';

// Import modal components
import ViewGenerationModal from './modals/ViewGenerationModal';
import CreateGenerationModal from './modals/CreateGenerationModal';
import GenerationImageModal from './modals/GenerationImageModal';
import KidImageSelectionModal from './modals/KidImageSelectionModal';
import OrderHelpModal from './modals/OrderHelpModal';
import AuthenticatedAssetImage from './AuthenticatedAssetImage';
import GiftBoxModal from './modals/GiftBoxModal';
import GiftNoteModal from './modals/GiftNoteModal';

// Define SERVER_URL constant
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

if (!SERVER_URL) {
  console.error('REACT_APP_SERVER_URL environment variable is not set');
}

const normalizeGenerationStatus = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'reprinting') return 'approved';
  if (normalized === 'stuck' || normalized === 'help') return 'pending';
  return normalized;
};

const GENERATION_STATUS_TRANSITIONS = {
  new: ['new', 'face_generating', 'face_pending', 'cancelled'],
  face_generating: ['face_generating', 'face_pending', 'cancelled'],
  face_pending: ['face_pending', 'new', 'trial', 'pending', 'cancelled'],
  trial: ['trial', 'face_generating', 'face_pending', 'pending', 'cancelled'],
  pending: ['pending', 'new', 'face_pending', 'trial', 'completed', 'cancelled'],
  completed: ['completed', 'new', 'face_pending', 'approved', 'cancelled'],
  approved: ['approved', 'completed', 'face_pending', 'new', 'out_for_printing', 'cancelled'],
  out_for_printing: ['out_for_printing', 'new', 'face_pending', 'printed', 'cancelled'],
  printed: ['printed'],
  cancelled: ['cancelled', 'new', 'face_generating', 'pending', 'completed', 'approved', 'out_for_printing', 'printed']
};

const GENERATION_STATUS_LABELS = {
  new: 'New',
  face_generating: 'Face Generating',
  face_pending: 'Face Pending',
  trial: 'Trial',
  pending: 'Pending',
  completed: 'Completed',
  approved: 'Approved',
  out_for_printing: 'Out for Printing',
  printed: 'Printed',
  cancelled: 'Cancelled'
};

const getGenerationIssue = (generation) => {
  const generationId = String(generation?.generation_id || '');
  const legacyStatus = String(generation?.status || '').toLowerCase();
  const localFlags = [
    ...(Array.isArray(generation?.flags) ? generation.flags : []),
    ...(Array.isArray(generation?.generation_flags) ? generation.generation_flags : [])
  ].filter(flag => {
    if (flag?.resolved) return false;
    const flagGenerationId = flag?.generation_id || flag?.metadata?.generation_id;
    return !flagGenerationId || String(flagGenerationId) === generationId;
  });
  const orderFlags = [
    ...(Array.isArray(generation?.order_flags) ? generation.order_flags : []),
    ...(Array.isArray(generation?.order?.flags) ? generation.order.flags : [])
  ].filter(flag => {
    if (flag?.resolved) return false;
    const flagGenerationId = flag?.generation_id || flag?.metadata?.generation_id;
    return flagGenerationId && String(flagGenerationId) === generationId;
  });
  const flags = [...localFlags, ...orderFlags];
  const helpFlag = flags.find(flag =>
    ['help', 'help_request'].includes(String(flag?.flag_type || flag?.type || '').toLowerCase())
  );
  if (helpFlag) {
    return { type: 'help', reason: helpFlag.description || helpFlag.notes || 'Help requested' };
  }

  const stuckFlag = flags.find(flag =>
    ['stuck', 'generation_stuck', 'technical_problem'].includes(
      String(flag?.flag_type || flag?.type || '').toLowerCase()
    )
  );
  if (stuckFlag || generation?.is_stuck || generation?.stuck_reason) {
    return {
      type: 'stuck',
      reason: stuckFlag?.description || stuckFlag?.notes || generation?.stuck_reason || 'Generation needs attention'
    };
  }

  return ['stuck', 'help'].includes(legacyStatus)
    ? { type: legacyStatus, reason: legacyStatus === 'help' ? 'Help requested' : 'Generation needs attention' }
    : null;
};


// Generations tab content (duplicate of renderPreviewersContent, but for Generations)
const GenerationContentPage = ({ setToastMessage, setToastActive, fixedSource, excludeSource }) => {
  const headings = [
    '', // Checkbox column
    'Order ID',
    'Kid Name',
    'Kid Image',
    'Story Name',
    'Story Image',
    'Created Date',
    'Status',
    'Actions',
  ];

  // Add state for Generations tab
  const [generations, setGenerations] = useState([]);
  const [generationStatusCounts, setGenerationStatusCounts] = useState({});
  const [isLoadingGenerations, setIsLoadingGenerations] = useState(false);
  const [generationsStatusTab, setGenerationsStatusTab] = useState('all');
  const [generationsSearchQuery, setGenerationsSearchQuery] = useState('');
  const [isGenerationsImageModalOpen, setIsGenerationsImageModalOpen] = useState(false);
  const [generationsModalImageUrl, setGenerationsModalImageUrl] = useState('');
  const [generationsModalImageAlt, setGenerationsModalImageAlt] = useState('');
  const [isViewGenerationModalOpen, setIsViewGenerationModalOpen] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [currentGenerationImageIndex, setCurrentGenerationImageIndex] = useState(0);

  const [isCreateGenerationModalOpen, setIsCreateGenerationModalOpen] = useState(false);
  const [isKidImageSelectionModalOpen, setIsKidImageSelectionModalOpen] = useState(false);
  const [selectedGenerationForKidImages, setSelectedGenerationForKidImages] = useState(null);
  const [stories, setStories] = useState([]);
  const [isLoadingStories, setIsLoadingStories] = useState(false);
  const [regeneratingMissingById, setRegeneratingMissingById] = useState({});
  const [regeneratingTextById, setRegeneratingTextById] = useState({});
  const [isOrderHelpModalOpen, setIsOrderHelpModalOpen] = useState(false);
  const [selectedGenerationForHelp, setSelectedGenerationForHelp] = useState(null);
  const [isSubmittingHelp, setIsSubmittingHelp] = useState(false);
  const [isGiftBoxModalOpen, setIsGiftBoxModalOpen] = useState(false);
  const [selectedGenerationForGiftBox, setSelectedGenerationForGiftBox] = useState(null);
  const [isGiftNoteModalOpen, setIsGiftNoteModalOpen] = useState(false);
  const [selectedGenerationForGiftNote, setSelectedGenerationForGiftNote] = useState(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const bulkStatusOptions = useMemo(() => {
    const selectedGenerationStatuses = generations
      .filter(generation => selectedIds.includes(String(generation._id)))
      .map(generation => normalizeGenerationStatus(generation.status));
    return Object.keys(GENERATION_STATUS_LABELS)
      .filter(status => selectedGenerationStatuses.length > 0 && selectedGenerationStatuses.every(
        currentStatus => GENERATION_STATUS_TRANSITIONS[currentStatus]?.includes(status)
      ));
  }, [generations, selectedIds]);

  // Date filter state
  const [dateFilterType, setDateFilterType] = useState('created_at'); // 'created_at' or 'approved_at'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [count, setCount] = useState(0);


  // Stories functions
  const fetchStories = useCallback(async () => {
    setIsLoadingStories(true);
    try {
      const data = await storiesService.getAllStories();
      const storiesArray = Array.isArray(data) ? data : [];
      setStories(storiesArray);
    } catch (err) {
      console.error('Fetch error:', err);
      setStories([]); // Ensure stories is always an array
    } finally {
      setIsLoadingStories(false);
    }
  }, []);

  // Fetch generations data
  const fetchGenerations = useCallback(async () => {
    setIsLoadingGenerations(true);
    try {
      const result = await generationsService.getAllGenerations({
        page,
        limit,
        status: ['stuck', 'help'].includes(generationsStatusTab)
          ? 'pending'
          : generationsStatusTab,
        issue: ['stuck', 'help'].includes(generationsStatusTab)
          ? generationsStatusTab
          : undefined,
        q: generationsSearchQuery || undefined,
        dateFilterType: dateFilterType || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        order_source: fixedSource || undefined,
        exclude_order_source: excludeSource || undefined,
      });
      const resultGenerations = Array.isArray(result?.data) ? result.data : [];
      const visibleGenerations = ['stuck', 'help'].includes(generationsStatusTab)
        ? resultGenerations.filter(generation => getGenerationIssue(generation)?.type === generationsStatusTab)
        : resultGenerations;
      setGenerations(visibleGenerations);
      if (result) {
        setGenerationStatusCounts(result.statusCounts || {});
        setTotal(Number(result.total || 0));
        setPage(Number(result.page || 1));
        setLimit(Number(result.limit || limit));
        setCount(Number(result.count || visibleGenerations.length));
        setPageCount(Number(result.pageCount || 0));
        setHasNext(Boolean(result.hasNext));
        setHasPrev(Boolean(result.hasPrev));
      }
    } catch (err) {
      setGenerations([]);
      setGenerationStatusCounts({});
      setTotal(0);
      setPage(1);
      setCount(0);
      setPageCount(0);
      setHasNext(false);
      setHasPrev(false);
    } finally {
      setIsLoadingGenerations(false);
    }
  }, [page, limit, generationsStatusTab, generationsSearchQuery, dateFilterType, startDate, endDate, fixedSource, excludeSource]);

  // Fetch generations when tab is selected
  useEffect(() => {
    fetchGenerations();
    fetchStories();
  }, [fetchGenerations, fetchStories]);

  // Add event listener for Create Generation modal
  useEffect(() => {
    const handleOpenCreateGenerationModal = () => {
      setIsCreateGenerationModalOpen(true);
    };

    window.addEventListener('openCreateGenerationModal', handleOpenCreateGenerationModal);

    return () => {
      window.removeEventListener('openCreateGenerationModal', handleOpenCreateGenerationModal);
    };
  }, []);

  // Handler for Generations image click
  const handleGenerationsImageClick = useCallback((imageUrl, imageAlt) => {
    setGenerationsModalImageUrl(imageUrl);
    setGenerationsModalImageAlt(imageAlt);
    setIsGenerationsImageModalOpen(true);
  }, []);

  // Handler for View Generation modal
  const handleViewGeneration = useCallback((generation) => {
    setSelectedGeneration(generation);
    setIsViewGenerationModalOpen(true);
    setCurrentGenerationImageIndex(0);
  }, []);

  // Handler for Kid Image Selection modal
  const handleSelectKidImage = useCallback((generation) => {
    setSelectedGenerationForKidImages(generation);
    setIsKidImageSelectionModalOpen(true);
  }, []);

  // Handler for Order Help modal
  const handleOrderHelp = useCallback((generation) => {
    setSelectedGenerationForHelp(generation);
    setIsOrderHelpModalOpen(true);
  }, []);

  // Handler for submitting order help
  const handleSubmitOrderHelp = useCallback(async (helpNotes) => {
    if (!selectedGenerationForHelp?.order_id) {
      throw new Error('No order ID found for this generation');
    }

    setIsSubmittingHelp(true);
    try {
      await generationsService.updateShopifyOrderWithHelp(
        selectedGenerationForHelp.order_id,
        helpNotes,
        selectedGenerationForHelp.generation_id
      );
      setToastMessage('Order help request submitted successfully');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      fetchGenerations();
    } catch (error) {
      setToastMessage(`Error: ${error?.message || 'Failed to submit help request'}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
      throw error; // Re-throw to prevent modal from closing
    } finally {
      setIsSubmittingHelp(false);
    }
  }, [selectedGenerationForHelp, setToastMessage, setToastActive, fetchGenerations]);

  // Handler for checkbox selection
  const handleCheckboxChange = useCallback((genId, checked) => {
    setSelectedIds(prev => {
      if (checked) {
        return [...prev, genId];
      } else {
        return prev.filter(id => id !== genId);
      }
    });
  }, []);

  // Handler for select all checkbox
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      // All generations on current page are already filtered by backend
      setSelectedIds(generations.map(gen => gen._id));
    } else {
      setSelectedIds([]);
    }
  }, [generations]);

  // Handler for bulk status update
  const handleBulkStatusUpdate = useCallback(async () => {
    if (selectedIds.length === 0) {
      setToastMessage('Please select at least one generation');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    if (!bulkStatus) {
      setToastMessage('Please select a status');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }
    if (!bulkStatusOptions.includes(bulkStatus)) {
      setBulkStatus('');
      setToastMessage('The selected status is not valid for every selected generation');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to update ${selectedIds.length} generation(s) to status "${bulkStatus}"?`
    );
    if (!confirmed) return;

    setIsBulkUpdating(true);
    try {
      const result = await generationsService.bulkManualUpdateStatus(selectedIds, bulkStatus);
      setToastMessage(result.message || `${selectedIds.length} generation(s) updated successfully`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      setSelectedIds([]);
      setBulkStatus('');
      fetchGenerations();
    } catch (error) {
      setToastMessage(`Error: ${error?.message || 'Failed to update generations'}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
    } finally {
      setIsBulkUpdating(false);
    }
  }, [selectedIds, bulkStatus, bulkStatusOptions, fetchGenerations, setToastMessage, setToastActive]);

  // Clear selection when changing tabs or search
  useEffect(() => {
    setSelectedIds([]);
  }, [generationsStatusTab, generationsSearchQuery, page]);

  // Pusher: listen for real-time gift box completion events
  useEffect(() => {
    const pusherClient = new Pusher('45e734cbf0eb7fcc3b2a', {
      cluster: 'eu',
    });

    const channel = pusherClient.subscribe('GiftBox');

    channel.bind('gift-box-completed', (data) => {
      if (!data?.generation_id) return;

      // Patch the matching row in the table without a full refetch
      setGenerations((prev) =>
        prev.map((g) =>
          g.generation_id === data.generation_id
            ? {
                ...g,
                gift_box_status: data.gift_box_status || 'completed',
                gift_box_image_url: data.gift_box_image_url || g.gift_box_image_url,
              }
            : g
        )
      );

      // Also update the open GiftBoxModal's generation if it matches
      setSelectedGenerationForGiftBox((prev) => {
        if (prev?.generation_id === data.generation_id) {
          return {
            ...prev,
            gift_box_status: data.gift_box_status || 'completed',
            gift_box_image_url: data.gift_box_image_url || prev.gift_box_image_url,
          };
        }
        return prev;
      });

      setToastMessage('🎁 Gift box image is ready!');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
    });

    return () => {
      channel.unbind_all();
      pusherClient.unsubscribe('GiftBox');
      pusherClient.disconnect();
    };
  }, [setToastMessage, setToastActive]);

  const derivedPageCount = Math.max(1, Math.ceil(Number(total || 0) / Number(limit || 1)));

  return (
    <Layout style={{ marginBottom: '24px' }}>
      <Layout.Section>
        <Card>
          {/* Filter/Status Tabs and Search Bar Row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0 16px' }}>
            {/* Filter Tabs */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <ButtonGroup>
                {GENERATION_STATUS_TABS.map(tab => (
                  <Button
                    key={tab.value}
                    onClick={() => { setGenerationsStatusTab(tab.value); setPage(1); }}
                    variant="secondary"
                    tone={generationsStatusTab === tab.value ? 'success' : undefined}
                  >
                    {getGenerationStatusTabLabel(tab.label, generationStatusCounts[tab.value])}
                  </Button>
                ))}
              </ButtonGroup>
              {/* Refresh Button */}
              <Button
                icon={RefreshMajor}
                onClick={fetchGenerations}
                variant="tertiary"
                tone="base"
                accessibilityLabel="Refresh generations"
                style={{ marginLeft: 8 }}
              />
            </div>
            {/* Search Bar aligned right, styled like Stories/Scenes */}
            <div style={{ minWidth: 260 }}>
              <TextField
                label="Search by order ID, kid name, story, or generation ID..."
                value={generationsSearchQuery}
                onChange={(val) => {
                  setPage(1);
                  setGenerationsSearchQuery(val);
                }}
                placeholder="Search by order ID, kid name, story, or generation ID..."
                autoComplete="off"
                prefix={
                  <span className="Polaris-Icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="9" cy="9" r="7.5" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" />
                      <path d="M15 15L18 18" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </span>
                }
                labelHidden
              />
            </div>
          </div>

          {/* Date Filter Section */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e3e3e3',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <Text variant="bodyMd" as="span" fontWeight="semibold">
              Filter by Date:
            </Text>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <select
                value={dateFilterType}
                onChange={(e) => {
                  setDateFilterType(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #c4cdd5',
                  background: 'white',
                  fontSize: '14px',
                  minWidth: '140px'
                }}
              >
                <option value="created_at">Created Date</option>
                <option value="updated_at">Updated Date</option>
                <option value="approved_at">Approval Date</option>
                <option value="out_for_printing_at">Out for Printing Date</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text variant="bodySm" as="span">From:</Text>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #c4cdd5',
                  background: 'white',
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text variant="bodySm" as="span">To:</Text>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #c4cdd5',
                  background: 'white',
                  fontSize: '14px',
                }}
              />
            </div>

            {(startDate || endDate) && (
              <Button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setPage(1);
                }}
                variant="tertiary"
                tone="critical"
              >
                Clear Dates
              </Button>
            )}
          </div>

          {/* Bulk Actions Toolbar */}
          {selectedIds.length > 0 && (
            <div style={{
              padding: '16px',
              background: '#f6f6f7',
              borderBottom: '1px solid #e3e3e3',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                {selectedIds.length} selected
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                <select
                  value={bulkStatusOptions.includes(bulkStatus) ? bulkStatus : ''}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #c4cdd5',
                    background: 'white',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                >
                  <option value="">Select status...</option>
                  {bulkStatusOptions.map(status => (
                    <option key={status} value={status}>
                      {GENERATION_STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
                <Button
                  onClick={handleBulkStatusUpdate}
                  loading={isBulkUpdating}
                  disabled={!bulkStatus || !bulkStatusOptions.includes(bulkStatus)}
                  variant="primary"
                >
                  Update Status
                </Button>
                <Button
                  onClick={() => setSelectedIds([])}
                  variant="tertiary"
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}


          {/* IndexTable Implementation */}
          <div style={{ marginTop: 16 }}>
            <IndexTable
              resourceName={{ singular: 'generation', plural: 'generations' }}
              itemCount={generations.length}
              selectedItemsCount={
                selectedIds.length === generations.length
                  ? 'All'
                  : selectedIds.length
              }
              onSelectionChange={(selectionType) => {
                if (selectionType === 'all') {
                  handleSelectAll(true);
                } else if (selectionType === 'page') {
                  // logic for page selection if needed, currently handled by bulk selection custom logic typically
                  handleSelectAll(true);
                } else {
                  // Empty selection
                  handleSelectAll(false);
                }
              }}
              headings={[
                { title: 'Order ID' },
                { title: 'Kid Name' },
                { title: 'Kid Image' },
                { title: 'Story Name' },
                { title: 'Story Image' },
                { title: 'Created Date' },
                { title: 'Status' },
                { title: 'Actions' },
              ]}
              loading={isLoadingGenerations}
              emptyState={
                <div style={{ padding: '60px 16px', textAlign: 'center' }}>
                  <BlockStack gap="400" align="center">
                    <Icon source={ContentMinor} color="subdued" className="empty-state-big-icon" />
                    <Text variant="bodyMd" tone="subdued">No records found</Text>
                  </BlockStack>
                </div>
              }
            >
              {generations.map((gen, index) => {
                const kidImage = gen.kid_image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png';
                const kidImageThumb = getThumbnailUrl(kidImage);
                const storyName = gen.story && gen.story.title ? gen.story.title : (gen.story_slug || '');
                const addonLabels = getAddonLabels(gen);
                const storyCover = (gen.generated_scenes && gen.generated_scenes[0] && gen.generated_scenes[0].processedImageUrl) || '';
                const storyCoverThumb = storyCover ? getThumbnailUrl(storyCover) : '';
                const status = normalizeGenerationStatus(gen.status);
                const generationIssue = getGenerationIssue(gen);
                const missingSceneOrders = getMissingSceneOrders(gen);
                const recoveryRunning = isMissingSceneRecoveryRunning(gen);
                const formatCairoDate = (dateString) => {
                  if (!dateString) return { date: '-', time: '' };
                  try {
                    const date = new Date(dateString);
                    const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Africa/Cairo' });
                    let timePart = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Africa/Cairo' });
                    timePart = timePart.replace(/am|pm/i, match => match.toUpperCase());
                    return { date: datePart, time: timePart };
                  } catch {
                    return { date: '-', time: '' };
                  }
                };
                const cairoDateObj = formatCairoDate(gen.created_at);

                return (
                  <IndexTable.Row
                    id={gen._id}
                    key={gen._id}
                    selected={selectedIds.includes(gen._id)}
                    position={index}
                    onClick={() => handleCheckboxChange(gen._id, !selectedIds.includes(gen._id))}
                    tone={gen.is_express ? 'critical' : undefined}
                  >
                    <IndexTable.Cell>
                      {gen.order_id ? (
                        <a
                          href={`/orders/${encodeURIComponent(gen.order_id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Text variant="bodyMd" fontWeight="bold" as="span">
                            {gen.order_id}
                          </Text>
                        </a>
                      ) : (
                        <Text variant="bodyMd" fontWeight="bold" as="span">-</Text>
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>{gen.kid_name}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <div
                        style={{
                          cursor: 'pointer',
                          width: 48,
                          height: 48,
                          borderRadius: 8,
                          overflow: 'hidden',
                          background: '#f6f6f7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'transform 0.2s ease-in-out'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerationsImageClick(kidImage, gen.kid_name);
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      >
                        <AuthenticatedAssetImage
                          src={kidImageThumb}
                          fallbackSrc={kidImage}
                          assetSize="thumbnail"
                          alt={gen.kid_name}
                          style={{ width: 48, height: 48, objectFit: 'cover' }}
                          loading="lazy"
                        />
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <div style={{ maxWidth: 240, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        <BlockStack gap="100">
                          <Text as="span">{storyName}</Text>
                          {addonLabels.length > 0 && (
                            <InlineStack gap="100">
                              {addonLabels.map(label => (
                                <Badge
                                  key={label}
                                  tone={label === 'Express'
                                    ? 'critical'
                                    : label === 'Premium Printing'
                                      ? 'attention'
                                      : label === 'Gift Box'
                                        ? 'success'
                                        : 'info'}
                                  size="small"
                                >
                                  {label}
                                </Badge>
                              ))}
                            </InlineStack>
                          )}
                        </BlockStack>
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {storyCover ? (
                        <div
                          style={{
                            cursor: 'pointer',
                            width: 48,
                            height: 48,
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: '#f6f6f7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            transition: 'transform 0.2s ease-in-out'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerationsImageClick(storyCover, storyName);
                          }}
                          onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        >
                          <AuthenticatedAssetImage
                            src={storyCoverThumb}
                            fallbackSrc={storyCover}
                            assetSize="thumbnail"
                            alt="Story Cover"
                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <div style={{ lineHeight: 1.2 }}>
                        <span>{cairoDateObj.date}</span><br />
                        <span>{cairoDateObj.time}</span>
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <div style={{ width: 150, maxWidth: 150, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        {(() => {
                          const issueReason = generationIssue?.reason;
                          const lowerStatus = status;
                          const { tone, label } = recoveryRunning ? {
                            tone: 'attention',
                            label: 'Recovering'
                          } : generationIssue && lowerStatus === 'pending' ? {
                            tone: 'critical',
                            label: generationIssue.type === 'help' ? 'Help' : 'Stuck'
                          } : (function () {
                            switch (lowerStatus) {
                              case 'approved':
                                return { tone: 'success', label: 'Approved' };
                              case 'completed':
                                return { tone: 'success', label: 'Completed' };
                              case 'out_for_printing':
                                return { tone: 'info', label: 'Out for Printing' };
                              case 'printed':
                                return { tone: 'success', label: 'Printed' };
                              case 'pending':
                                return { tone: 'attention', label: 'Pending' };
                              case 'face_pending':
                                return { tone: 'info', label: 'Face Pending' };
                              case 'trial':
                                return { tone: 'warning', label: 'Trial' };
                              case 'face_generating':
                                return { tone: 'info', label: 'Face Generating' };
                              case 'new':
                                return { tone: 'info', label: 'New' };
                              case 'cancelled':
                                return { tone: 'critical', label: 'Cancelled' };
                              default:
                                return { tone: 'info', label: gen.status || 'Unknown' };
                            }
                          })();
                          const badgeNode = (
                            <Badge tone={tone}>
                              {label}
                            </Badge>
                          );
                          if (lowerStatus === 'pending' || recoveryRunning) {
                            let displayedMissingOrders = missingSceneOrders;
                            if (displayedMissingOrders.length === 0 && typeof issueReason === 'string') {
                              const matches = issueReason.match(/Scene\s+(\d+)/gi) || [];
                              displayedMissingOrders = matches.map(m => (m.match(/\d+/)?.[0] || '')).filter(Boolean);
                            }
                            return (
                              <div style={{ lineHeight: 1.3 }}>
                                {issueReason ? (
                                  <Tooltip content={issueReason}>{badgeNode}</Tooltip>
                                ) : (
                                  badgeNode
                                )}
                                {displayedMissingOrders.length > 0 && (
                                  <div style={{ marginTop: 6, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                    <Text variant="bodySm" tone="subdued">
                                      Missing scenes: {displayedMissingOrders.join(', ')}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return badgeNode;
                        })()}
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="100" align="center">
                        {(status === 'new' || generationIssue?.type === 'help') ? (
                          <>
                            <Tooltip content="Select Kid Image">
                              <Button
                                icon={<span style={{ fontWeight: 700, fontSize: 14 }}>📷</span>}
                                size="slim"
                                tone="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectKidImage(gen);
                                }}
                              />
                            </Tooltip>
                            {gen.order_id && (
                              <Tooltip content="Request Order Help">
                                <Button
                                  icon={QuestionMarkMajor}
                                  size="slim"
                                  tone="base"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOrderHelp(gen);
                                  }}
                                />
                              </Tooltip>
                            )}
                          </>
                        ) : (
                          <>
                            <Tooltip content="View">
                              <Button
                                icon={ViewMajor}
                                size="slim"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewGeneration(gen);
                                }}
                              />
                            </Tooltip>

                            {status !== 'pending' && (
                              <Tooltip content="Regenerate text overlays">
                                <Button
                                  icon={<span style={{ fontWeight: 700, fontSize: 14 }}>T</span>}
                                  size="slim"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const genId = gen?.generation_id;
                                    if (!genId) return;
                                    setRegeneratingTextById(prev => ({ ...prev, [genId]: true }));
                                    try {
                                      const resp = await fetch(`${SERVER_URL}/generation-stories/regenerate-text-scenes`, {
                                        method: 'POST',
                                        headers: {
                                          ...authService.getAuthHeaders(),
                                          'Content-Type': 'application/json',
                                        },
                                        body: JSON.stringify({ generationId: genId }),
                                      });
                                      let data = {};
                                      try { data = await resp.clone().json(); } catch { }
                                      if (!resp.ok) {
                                        const msg = data?.error || data?.message || `HTTP error! status: ${resp.status}`;
                                        throw new Error(msg);
                                      }
                                      setToastMessage('Regenerating text overlays started');
                                      setToastActive(true);
                                      setTimeout(() => setToastActive(false), 3000);
                                      fetchGenerations();
                                    } catch (err) {
                                      setToastMessage(`Error: ${err?.message || 'Failed to regenerate text overlays'}`);
                                      setToastActive(true);
                                      setTimeout(() => setToastActive(false), 4000);
                                    } finally {
                                      setRegeneratingTextById(prev => ({ ...prev, [genId]: false }));
                                    }
                                  }}
                                  loading={!!regeneratingTextById[gen?.generation_id]}
                                />
                              </Tooltip>
                            )}
                            {missingSceneOrders.length > 0 && ['pending', 'completed'].includes(status) && (
                              <Tooltip content={recoveryRunning ? 'Missing-scene recovery is running' : `Regenerate missing scenes: ${missingSceneOrders.join(', ')}`}>
                                <Button
                                  icon={RefreshMajor}
                                  size="slim"
                                  disabled={recoveryRunning}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const genId = gen?.generation_id;
                                    if (!genId) return;
                                    setRegeneratingMissingById(prev => ({ ...prev, [genId]: true }));
                                    try {
                                      const resp = await fetch(`${SERVER_URL}/generation-stories/regenerate-missing`, {
                                        method: 'POST',
                                        headers: {
                                          ...authService.getAuthHeaders(),
                                          'Content-Type': 'application/json',
                                        },
                                        body: JSON.stringify({ generationId: genId }),
                                      });
                                      let data = {};
                                      try { data = await resp.clone().json(); } catch { }
                                      if (!resp.ok) {
                                        const msg = data?.error || data?.message || `HTTP error! status: ${resp.status}`;
                                        throw new Error(msg);
                                      }
                                      setToastMessage(`Recovering missing scenes: ${(data?.missingOrders || missingSceneOrders).join(', ')}`);
                                      setToastActive(true);
                                      setTimeout(() => setToastActive(false), 3000);
                                      fetchGenerations();
                                    } catch (err) {
                                      setToastMessage(`Error: ${err?.message || 'Failed to regenerate missing scenes'}`);
                                      setToastActive(true);
                                      setTimeout(() => setToastActive(false), 4000);
                                    } finally {
                                      setRegeneratingMissingById(prev => ({ ...prev, [genId]: false }));
                                    }
                                  }}
                                  loading={recoveryRunning || !!regeneratingMissingById[gen?.generation_id]}
                                />
                              </Tooltip>
                            )}
                            {gen.is_gift && (
                              <Tooltip content={
                                gen.gift_box_image_url ? 'View Gift Box' :
                                (gen.gift_box_status === 'queued' || gen.gift_box_status === 'generating') ? 'Gift Box Generating...' :
                                'Generate Gift Box'
                              }>
                                <Button
                                  icon={<span style={{ fontWeight: 700, fontSize: 14 }}>🎁</span>}
                                  size="slim"
                                  tone={
                                    gen.gift_box_image_url ? 'success' :
                                    (gen.gift_box_status === 'queued' || gen.gift_box_status === 'generating') ? 'caution' :
                                    'base'
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedGenerationForGiftBox(gen);
                                    setIsGiftBoxModalOpen(true);
                                  }}
                                  loading={gen.gift_box_status === 'queued' || gen.gift_box_status === 'generating'}
                                />
                              </Tooltip>
                            )}
                            {gen.has_gift_note && (
                              <Tooltip content={
                                gen.gift_note_pdf_url ? 'View Gift Note' :
                                gen.gift_note_status === 'processing' ? 'Gift Note Generating...' :
                                'Generate Gift Note'
                              }>
                                <Button
                                  icon={<span style={{ fontWeight: 700, fontSize: 14 }}>📝</span>}
                                  size="slim"
                                  tone={
                                    gen.gift_note_pdf_url ? 'success' :
                                    gen.gift_note_status === 'processing' ? 'caution' :
                                    'base'
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedGenerationForGiftNote(gen);
                                    setIsGiftNoteModalOpen(true);
                                  }}
                                  loading={gen.gift_note_status === 'processing'}
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                      </InlineStack>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          </div>
          {/* Segmented Pagination Controls (matching PreviewerContentPage) */}
          {(total > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '24px 0' }}>
              <ButtonGroup variant="segmented">
                <Button
                  icon={ChevronLeftMinor}
                  disabled={!hasPrev || page === 1}
                  onClick={() => hasPrev && setPage(page - 1)}
                />
                {(() => {
                  let start = Math.max(1, page - 2);
                  let end = Math.min(derivedPageCount, start + 4);
                  if (end - start < 4) start = Math.max(1, end - 4);
                  const pages = [];
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <Button
                        key={i}
                        pressed={i === page}
                        onClick={() => setPage(i)}
                        variant={i === page ? 'secondary' : 'secondary'}
                      >
                        {i}
                      </Button>
                    );
                  }
                  return pages;
                })()}
                <Button
                  icon={ChevronRightMinor}
                  disabled={!hasNext || page === derivedPageCount}
                  onClick={() => hasNext && setPage(page + 1)}
                />
              </ButtonGroup>
              <div style={{ marginTop: 8, marginBottom: 16, color: '#6d7175', fontSize: 13 }}>
                {total > 0 ? ((page - 1) * limit) + 1 : 0}
                –
                {total > 0 ? Math.min(((page - 1) * limit) + count, total) : 0}
                {' of '}
                {total}
                {' (Page '}{page}{' of '}{derivedPageCount}{')'}
              </div>
            </div>
          )}
        </Card>
      </Layout.Section>
      {/* View Generation Modal */}
      <ViewGenerationModal
        isOpen={isViewGenerationModalOpen}
        onClose={() => setIsViewGenerationModalOpen(false)}
        selectedGeneration={selectedGeneration}
        setToastMessage={setToastMessage}
        setToastActive={setToastActive}
      />

      {/* Create Generation Modal */}
      <CreateGenerationModal
        isOpen={isCreateGenerationModalOpen}
        onClose={() => setIsCreateGenerationModalOpen(false)}
        setToastMessage={setToastMessage}
        setToastActive={setToastActive}
        onGenerationCreated={fetchGenerations}
      />

      {/* Generations Image Modal */}
      <GenerationImageModal
        isOpen={isGenerationsImageModalOpen}
        onClose={() => setIsGenerationsImageModalOpen(false)}
        imageUrl={generationsModalImageUrl}
        imageAlt={generationsModalImageAlt}
      />

      {/* Kid Image Selection Modal */}
      <KidImageSelectionModal
        isOpen={isKidImageSelectionModalOpen}
        onClose={() => setIsKidImageSelectionModalOpen(false)}
        generation={selectedGenerationForKidImages}
        setToastMessage={setToastMessage}
        setToastActive={setToastActive}
        onGenerationUpdated={fetchGenerations}
      />

      {/* Order Help Modal */}
      <OrderHelpModal
        isOpen={isOrderHelpModalOpen}
        onClose={() => setIsOrderHelpModalOpen(false)}
        generation={selectedGenerationForHelp}
        onSubmit={handleSubmitOrderHelp}
        isSubmitting={isSubmittingHelp}
      />

      {/* Gift Box Modal */}
      <GiftBoxModal
        isOpen={isGiftBoxModalOpen}
        onClose={() => setIsGiftBoxModalOpen(false)}
        generation={selectedGenerationForGiftBox}
        setToastMessage={setToastMessage}
        setToastActive={setToastActive}
      />

      {/* Gift Note Modal */}
      <GiftNoteModal
        isOpen={isGiftNoteModalOpen}
        onClose={() => setIsGiftNoteModalOpen(false)}
        generation={selectedGenerationForGiftNote}
        setToastMessage={setToastMessage}
        setToastActive={setToastActive}
      />

    </Layout>
  );
};

export default GenerationContentPage;